import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, Award, Lightbulb } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Precision Craftsmanship',
      description: 'Every website is built with meticulous attention to detail. We sweat the small stuff so your users don\'t have to.',
    },
    {
      icon: Users,
      title: 'Partnership Mindset',
      description: 'We invest in understanding your business as deeply as you do. Your success is our success.',
    },
    {
      icon: Award,
      title: 'Premium Standards',
      description: 'We hold ourselves to the highest standards. If it doesn\'t meet our quality bar, it doesn\'t ship.',
    },
    {
      icon: Lightbulb,
      title: 'User-First Thinking',
      description: 'Great websites start with understanding users. We design experiences that feel natural and intuitive.',
    },
  ];

  return (
    <div data-testid="about-page" className="page-transition">
      {/* Hero Section */}
      <section className="pt-40 pb-20 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] to-[#050505]" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="overline">About Us</span>
            <h1 className="heading-hero text-[#F5F5F7] mt-4 mb-6">
              Premium Web
              <br />
              <span className="text-gradient-gold">Design Studio</span>
            </h1>
            <p className="body-large">
              Eleven11 Studios is a premium web design & development studio helping 
              businesses in the USA & Canada build high-performance websites.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-[#050505]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="overline">Our Philosophy</span>
              <h2 className="heading-section text-[#F5F5F7] mt-4 mb-6">
                Craftsmanship Over Templates
              </h2>
              <div className="space-y-6 text-[#A1A1AA] leading-relaxed">
                <p>
                  We started Eleven11 Studios with a simple belief: every business deserves a 
                  website that truly represents their brand. Too many websites look the same, 
                  built from the same templates, with the same generic feel.
                </p>
                <p>
                  We're different. We approach every project as craftspeople, not assembly 
                  line workers. We take the time to understand your business, your users, 
                  and what makes you unique. Then we build something that reflects that.
                </p>
                <p>
                  Every pixel matters. Every interaction counts. That's the standard we 
                  hold ourselves to, and it's why our clients trust us with their 
                  digital presence.
                </p>
              </div>
            </div>
            <div className="relative">
              <div 
                className="aspect-[4/5] bg-cover bg-center"
                style={{
                  backgroundImage: `url(https://images.unsplash.com/photo-1770992161088-7ad66282c9af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbHV4dXJ5JTIwY2luZW1hdGljJTIwb2ZmaWNlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NTE3Mjk3NHww&ixlib=rb-4.1.0&q=85)`,
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
              </div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 border border-[#D4AF37]/30" />
            </div>
          </div>
        </div>
      </section>

      {/* Selectivity Section */}
      <section className="py-20 bg-[#111111]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block border border-[#D4AF37]/30 px-8 py-6 mb-8">
              <p className="text-2xl md:text-3xl text-[#F5F5F7] font-medium leading-relaxed">
                "We don't work with everyone.
                <br />
                <span className="text-[#D4AF37]">Only businesses serious about quality."</span>
              </p>
            </div>
            <p className="text-[#A1A1AA] leading-relaxed">
              We intentionally keep our client roster small so we can give each project the 
              attention it deserves. This isn't a factory. It's a studio. If you're looking 
              for cheap, fast, or "good enough," we're probably not the right fit.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-[#050505]">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="overline">Our Values</span>
            <h2 className="heading-section text-[#F5F5F7] mt-4">
              What Drives Us
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <div 
                key={value.title}
                data-testid={`value-card-${index}`}
                className="premium-card p-8"
              >
                <value.icon className="w-10 h-10 text-[#D4AF37] mb-6" />
                <h3 className="text-xl font-semibold text-[#F5F5F7] mb-4">{value.title}</h3>
                <p className="text-[#A1A1AA] leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-[#0a0a0a]">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="overline">Our Process</span>
            <h2 className="heading-section text-[#F5F5F7] mt-4">
              How We Work
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'Understanding your business, goals, and users.' },
              { step: '02', title: 'Design', desc: 'Creating wireframes and visual concepts.' },
              { step: '03', title: 'Development', desc: 'Building with clean code and best practices.' },
              { step: '04', title: 'Launch', desc: 'Testing, optimization, and go-live support.' },
            ].map((item, index) => (
              <div key={item.step} className="text-center">
                <div className="text-5xl font-bold text-[#D4AF37]/20 mb-4">{item.step}</div>
                <h3 className="text-lg font-semibold text-[#F5F5F7] mb-2">{item.title}</h3>
                <p className="text-[#71717A] text-sm">{item.desc}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 right-0 w-8 h-px bg-white/10" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#050505]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="overline">Let's Connect</span>
            <h2 className="heading-section text-[#F5F5F7] mt-4 mb-6">
              Ready to Build Your Website?
            </h2>
            <p className="body-large mb-10">
              If you're looking for a team that cares about quality as much 
              as you do, let's talk about your project.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="btn-primary inline-flex"
            >
              Start Your Project
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
